import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import WebsiteStore from "../../WebsiteStore";
import { Layer as LayerNode, Rect, Stage as StageNode, Text, Image as ImageNode, Group as GroupNode } from "react-konva";
import KonvaPatternImage from "../KonvaNode/KonvaPatternImage";
import KonvaImage from "../KonvaNode/KonvaImage";
import { isBrowser, isNode } from "browser-or-node";
import LayerRenderer from "./LayerRenderer";
import { Layer } from 'konva/lib/Layer';
import rotatePoint from "../../Common/Utils/Math/rotatePoint";
import { useAppSelector } from "../../Common/_hooks/useAppSelector";
import { LabData, LabPartData } from "../../AppData/LabData";

type Props = {
  subproduct: string | null,
  subproductRef?: string,
  scene: string,
  part: string,
  partData: LabPartData,
  subproductRef_partData?: LabPartData,
  inputType: 'lab',
  outputType: 'texture' | 'render',
  labData: LabData,
}

export default function PartRenderer(props: Props) {
  const visibleLayers = useAppSelector(state => state.get('UIData').get('designLab').get('layers'));
  const backgroundColor = useAppSelector(state => state.get('UIData').get('designLab').get('backgroundColor'));
  const partRef = useRef<Layer | null>(null);

  const partOutData = props.partData[props.outputType];

  let clipX = 0,
    clipY = 0,
    clipWidth = partOutData.width,
    clipHeight = partOutData.height,
    rotation = partOutData.rotation ? partOutData.rotation : 0;

  //Crop the part so it doesn't overflow into other parts
  if (partOutData.crop) {
    clipX = partOutData.crop.x;
    clipY = partOutData.crop.y;
    clipWidth = partOutData.crop.width;
    clipHeight = partOutData.crop.height;
  }

  //Calculate bounding box based on rotation
  const origin = { x: 0, y: 0 };
  const a = rotatePoint(origin, { x: 0, y: 0 }, rotation);
  const b = rotatePoint(origin, { x: 0 + clipWidth, y: 0 }, rotation);
  const c = rotatePoint(origin, { x: 0 + clipWidth, y: 0 + clipHeight }, rotation);
  const d = rotatePoint(origin, { x: 0, y: 0 + clipHeight }, rotation);
  const boundingBox = {
    left: Math.min(a.x, b.x, c.x, d.x),
    top: Math.min(a.y, b.y, c.y, d.y),
  };

  //Add bounding box offset to our position
  let x = partOutData.x - boundingBox.left;
  let y = partOutData.y - boundingBox.top;

  //I/O scaling
  let scaleX = props.partData[props.outputType].width / props.partData[props.inputType].width;
  let scaleY = props.partData[props.outputType].height / props.partData[props.inputType].height;

  const renderLayer = useCallback((layerId: string) => {
    return <LayerRenderer
      key={layerId}
      layerId={layerId}
      subproduct={props.subproduct}
      subproductRef={props.subproductRef}
      scene={props.scene}
      part={props.part}
      partData={props.partData}
      subproductRef_partData={props.subproductRef_partData}
      inputType={props.inputType}
      outputType={props.outputType}
      labData={props.labData}
    />
  }, [props.labData, props.subproduct, props.subproductRef, props.subproductRef_partData, props.partData, props.scene, props.part, props.inputType, props.outputType])

  //TODO: Chunks are currently untested with rotation. Not sure if the part rotation should apply to each chunks at that would be kinda weird to calculate. Perhaps chunk rotation should ignore part rotation and be defined per part?
  const chunks = useMemo(() => {
    return partOutData.chunks ? partOutData.chunks : [{
      in: {
        x: 0,
        y: 0,
        width: clipWidth,
        height: clipHeight,
      },
      out: {
        x: 0,
        y: 0,
      },
    }]
  }, [partOutData, clipWidth, clipHeight])

  return <>
    {chunks.map((chunk, index) => {
      return <LayerNode
        key={index}
        ref={partRef}
        x={x + chunk.out.x}
        y={y + chunk.out.y}
        offsetX={clipX + chunk.in.x}
        offsetY={clipY + chunk.in.y}
        clipX={clipX + chunk.in.x}
        clipY={clipY + chunk.in.y}
        clipWidth={chunk.in.width}
        clipHeight={chunk.in.height}
        rotation={rotation}
        listening={false}
      >
        {!props.labData.transparent_texture ? <Rect
          width={chunk.in.width}
          height={chunk.in.height}
          x={clipX + chunk.in.x}
          y={clipY + chunk.in.y}
          fill={backgroundColor ? backgroundColor : "#fff"}
          listening={false}
        /> : null}
        <GroupNode
          scaleX={scaleX}
          scaleY={scaleY}
          listening={false}
        >
          {visibleLayers.keySeq().map(renderLayer)}
        </GroupNode>
      </LayerNode>
    })}
  </>
}
